import { useEffect, useId, useMemo } from 'react';
import {
  rankWith,
  ControlProps,
  isEnumControl,
  isVisible,
} from '@jsonforms/core';
import type { UISchemaElement } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { MenuItem, FormControl, InputLabel, Select, FormHelperText, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EnumValueHelpText } from '@/features/help/HelpReadMore';

/**
 * Renderer for static enum fields (schema.enum) with tooltip support.
 * Handles both required (type: "string") and optional (type: ["string", "null"]) enums.
 * Uses i18n keys (Enum:{enumType}:{value}) when enumType is available, falling back to enumNames.
 *
 * Honours the ui schema's `options.allowedValuesWhen` (DSL `allowedValuesWhen`): the offered values
 * narrow to the entry whose condition currently holds, so a field whose meaningful choices depend on
 * a sibling — a measure's storage, given whether it is entered or calculated — offers only those.
 * The schema keeps every value; this is what the form OFFERS.
 */

interface AllowedValuesEntry {
  condition: unknown;
  values: string[];
}

/**
 * The values this control may currently offer, or undefined when nothing narrows it. Evaluated with
 * JSONForms' own rule engine (a SHOW rule around the declared condition) so the semantics match
 * `visibleWhen` exactly rather than re-implementing condition matching.
 */
function allowedValuesFor(
  entries: AllowedValuesEntry[] | undefined,
  rootData: unknown,
  ajv: Parameters<typeof isVisible>[3] | undefined,
): string[] | undefined {
  if (!entries?.length || !ajv) return undefined;
  const match = entries.find(entry => {
    const probe = { type: 'Control', rule: { effect: 'SHOW', condition: entry.condition } } as unknown as UISchemaElement;
    return isVisible(probe, rootData, '', ajv, undefined);
  });
  return match?.values;
}

const StaticEnumRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, label, description, required, errors, enabled, visible, schema, uischema } = props;
  const { t } = useTranslation();
  const ctx = useJsonForms();
  // The entity the form edits: the form-schema generator titles a form's root schema with the entity's name.
  const formEntity = ctx.core?.schema?.title;

  /**
   * MUI renders <Select> as a `div[role="combobox"]`, NOT an <input>, so a bare <InputLabel> beside it
   * labels nothing: there is no `for`/`id` pair to bind them, and MUI only emits `aria-labelledby` when
   * given a `labelId`. Without this, every static-enum field in every generated form was a REQUIRED
   * control with NO accessible name — `getByLabel("Account Type *")` matched 0 elements while every
   * other field on the same form matched 1. A screen reader announced an unnamed combobox, and
   * clicking the label did not focus the control the way it does for all the other fields.
   *
   * `useId()` gives a stable, collision-free id per mounted control — this renderer is mounted once
   * per enum field, so a hard-coded id would collide on any form with two enums.
   *
   * Declared before the `!visible` early-return: hooks may not run conditionally.
   */
  const labelId = useId();
  const selectId = `${labelId}-select`;

  const declaredValues: string[] = useMemo(() => (schema as any)?.enum || [], [schema]);
  const rootData = ctx.core?.data;
  const ajv = ctx.core?.ajv;
  const allowedValues = useMemo(
    () => allowedValuesFor((uischema as any)?.options?.allowedValuesWhen, rootData, ajv),
    [uischema, rootData, ajv],
  );

  // A stored value the narrowed list no longer offers would sit in the control unlabelled and save
  // as-is. Clearing makes the user re-answer the question their other edit just reopened. Above the
  // `!visible` return with the other hooks: hooks may not run conditionally.
  useEffect(() => {
    if (allowedValues && data != null && data !== '' && !allowedValues.includes(data)) {
      handleChange(path, undefined);
    }
  }, [allowedValues, data, path, handleChange]);

  if (!visible) return null;

  const enumValues = allowedValues
    ? declaredValues.filter(v => allowedValues.includes(v))
    : declaredValues;
  const enumNames: string[] = (schema as any)?.enumNames || [];
  const enumOrdinals: number[] = (schema as any)?.enumOrdinals || [];
  const enumType: string | undefined = (schema as any)?.enumType;
  const showNumericCode: boolean = (schema as any)?.showNumericCode ?? false;

  const handleValueChange = (event: any) => {
    const val = event.target.value;
    handleChange(path, val === '' ? undefined : val);
  };

  const isUnresolved = description?.endsWith('.description') && !description.includes(' ');
  const hasTooltip = description && description !== label && !isUnresolved;

  return (
    <FormControl fullWidth required={required} error={!!errors} size="small" disabled={!enabled}>
      {hasTooltip ? (
        <Tooltip title={description} placement="top" arrow enterDelay={400}>
          <InputLabel id={labelId}>{label}</InputLabel>
        </Tooltip>
      ) : (
        <InputLabel id={labelId}>{label}</InputLabel>
      )}
      <Select
        labelId={labelId}
        id={selectId}
        // A value outside the narrowed list is on its way out (the effect above clears it); showing
        // it for that one render would draw an MUI out-of-range warning and an unlabelled selection.
        value={data && enumValues.includes(data) ? data : ''}
        onChange={handleValueChange}
        label={label}
        displayEmpty={!required}
      >
        {!required && (
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
        )}
        {enumValues.map((value) => {
          // enumNames/enumOrdinals are parallel to the SCHEMA's enum, so look the index up there —
          // a narrowed list would otherwise read another value's name.
          const index = declaredValues.indexOf(value);
          const displayName = enumType
            ? t(`Enum:${enumType}:${value}`, { defaultValue: enumNames[index] || value })
            : (enumNames[index] || value);
          const label = showNumericCode && enumOrdinals[index] !== undefined
            ? `${enumOrdinals[index]} - ${displayName}`
            : displayName;
          return (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          );
        })}
      </Select>
      {errors && <FormHelperText>{errors}</FormHelperText>}
      <EnumValueHelpText entity={formEntity} enumType={enumType} value={data} />
    </FormControl>
  );
};

// Priority 3: above Material defaults (1-2), same as tooltip text/number
export const staticEnumRendererEntry = {
  tester: rankWith(3, isEnumControl),
  renderer: withJsonFormsControlProps(StaticEnumRendererComponent),
};
